// RDS
import { ChevronDownIcon, LinkExternalIcon } from '@ramsey-design-system/icons';

// Components
import {
  FamilyIcon,
  ForSaleIcon,
  HealthInsuranceIcon,
  HomeAutoIcon,
  HomeBuySellIcon,
  HomeLoanIcon,
  HomeRefinancingIcon,
  IdTheft,
  LongTermCareIcon,
  MedicareServicesIcon,
  RetirementIcon,
  RibbonIcon,
  SmartTaxIcon,
  SpecialtyCommercialIcon,
  SpecialtyIdTheftIcon,
  SpecialtyUmbrellaIcon,
  SubHealthInsuranceIcon,
  TaxesCpaIcon,
  TaxesIcon,
  TermLife
} from '~fpu/shared/components/icons';

// Types
import {
  ResourceItems,
  SnapshotDisplayProperties,
  SnapshotType,
  TrustedCardProps
} from '../types';
import {
  LessonId,
  LessonNumber,
  Personality,
  PersonalityDetails
} from '~fpu/types';
import { PATHS } from '~fpu/config/constants';

export const BETTER_THAN_I_DESERVE_VIDEO = {
  src: 'https://streaming.ramseysolutions.net/video/ramsey-plus/dave-ramsey-testimony.mp4/hls/dave-ramsey-testimony.m3u8',
  thumbnail:
    'https://cdn.ramseysolutions.net/media/fpu/fpu_member/btid-thumbnail.png'
};

export const PASTORS_PATH_EXTERNAL_ORG_ID = 'CRUSADE-PASTORS4FREE';

export const GRACE_FELLOWSHIP_VIDEO = {
  src: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/pastors-path-video.mp4',
  thumbnail:
    'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/pastors-path-video-thumbnail.png',
  headerImage:
    'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/pastors-path-thumbnail.png',
  headerImageAlt: '30 years. 50,000 churches. Real kingdom impact.'
};

export const PASTORS_PATH_PURCHASE_IMAGE =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/all-access-banner-image.png';

const DEBT_PROPERTIES = {
  iconColor: '#35BD59', // Green 50
  iconBgColor: '#D9FAE1', // Green 8
  icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/debt-snowball.png'
};

const SAVINGS_PROPERTIES = {
  iconColor: '#FAAB19', // Amber 50
  iconBgColor: '#FFE6A8', // Amber 10
  icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/savings.png'
};

const CARDS_PROPERTIES = {
  iconColor: '#E64B40', // Red 50
  iconBgColor: '#FFC4B8', // Red 10
  icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/credit-cards-cut.png'
};

export const ALL_ACCESS = {
  SMART_TAX_LOGO:
    'https://cdn.ramseysolutions.net/ruby/ramsey-plus-web-app/assets/benefits/ramsey-plus-activation-page-image-03e996f76ebbbaf8abb39ce2d4e16309bb654ea69c4ed94e6b2344401c8ee0e2.png',
  WILLS:
    'https://www.ramseysolutions.com/retirement/will?int_cmpgn=ramseyplus-referrals&int_dept=projectfrontman_bu&int_dscpn=ramseyplus_memberbenefits_mamabearwills&int_fmt=button&int_lctn=ramseyplus_memberbenefits&lead_source=ramsey_plus#form-container',
  WILLS_LOGO:
    'https://cdn.ramseysolutions.net/media/ramsey-plus/app/benefits/assets/mama-bear.jpg',
  RAMSEY_TRUSTED_TAX_PROS:
    'https://www.ramseysolutions.com/taxes/tax-advisor?snid=trusted-services.taxes.tax-services.tax-advisor&lead_source=ramsey_plus',
  RAMSEY_TRUSTED_TAX_SERVICES:
    'https://www.ramseysolutions.com/taxes/tax-services?snid=trusted-services.taxes.tax-services&amp;lead_source=ramsey_plus',
  RAMSEY_TRUSTED_TAX_QUIZ: 'https://www.ramseysolutions.com/taxes/tax-quiz'
};

export const SNAPSHOT_DISPLAY_PROPERTIES: Record<
  SnapshotType,
  SnapshotDisplayProperties
> = {
  'initial-debt': { text: 'Initial Debt', ...DEBT_PROPERTIES },
  'initial-savings': { text: 'Initial Savings', ...SAVINGS_PROPERTIES },
  'initial-cards': { text: 'Initial Cards', ...CARDS_PROPERTIES },
  'progressive-debt': { text: 'Debt Paid', ...DEBT_PROPERTIES },
  'progressive-savings': { text: 'Money Saved', ...SAVINGS_PROPERTIES },
  'progressive-cards': { text: 'Credit Cards Cut', ...CARDS_PROPERTIES }
};

export const RESOURCE_ITEMS: ResourceItems = {
  RESOURCES: [
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/digital-workbook.png',
      heading: 'Digital Workbook',
      subheading: 'Get the PDF of the FPU workbook.',
      href: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/marketing/2026/fpu-books/03-2026-fpu_member-workbook.pdf',
      buttonText: 'View & Download'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/physical-workbook.png',
      heading: 'Physical Workbook',
      subheading: 'Buy a workbook today.',
      href: '',
      buttonText: 'Order Now',
      attribution: 'fpu',

      onClick: () => {
        if (window.zE) {
          window.zE('messenger', 'open');
        }
      },
      hasWorkbookDialog: true
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/home-base.png',
      heading: 'Real Estate Home Base',
      subheading: 'Get organized and prepared to buy or sell your home.',
      href: 'https://www.ramseysolutions.com/real-estate/resources?campaign_id=&int_cmpgn=HomeBase-FPU&int_cmpgn=HomeBaseFPU&int_dept=realestate_bu&int_dscpn=Real-Estate-Home-Base&int_fmt=button&int_lctn=HomeBase-FPU-Resources&lead_source=financial_peace',
      buttonText: 'Start Preparing',
      attribution: 'fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/fridge.png',
      heading: 'Meal Planner PDF',
      subheading: 'Save money each week.',
      href: 'https://cdn.ramseysolutions.net/media/b2c/personalities/rachel/grocery-savings-guide/downloads/weekly-meal-planner.pdf',
      buttonText: 'View & Download'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/groceries.png',
      heading: 'How Much to Budget for Groceries',
      subheading: 'This is one of our top pain points.',
      href: 'https://www.ramseysolutions.com/budgeting/average-cost-of-groceries',
      buttonText: 'Read the Article'
    }
  ],
  RAMSEY_SERVICES: [
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/coaching.png',
      heading: 'Ramsey Coaching',
      subheading:
        'Connect with a Ramsey Coach and get a free group financial coaching session.',
      href: 'https://www.ramseysolutions.com/ramseyplus/everydollar/group-coaching?source=fpu',
      buttonText: 'Free Group Coaching Session'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/trusted.png',
      heading: 'RamseyTrusted Pros',
      subheading: 'Get help with real estate, taxes, insurance and more.',
      href: `/app${PATHS.MEMBER_DASHBOARD.RAMSEY_TRUSTED}`,
      buttonText: 'Explore',
      attribution: 'trustedlead_fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/smartvestor.png',
      heading: 'SmartVestor Pros',
      subheading: 'Prepare for the future with an investing professional.',
      href: 'https://www.ramseysolutions.com/retirement/smartvestor?snid=trusted-services.retirement-and-investing.smartvestor-pros&lead_source=financial_peace',
      buttonText: 'Talk to a Pro',
      attribution: 'trustedlead_fpu'
    }
  ],
  TOOLS: [
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/net-worth.png',
      heading: 'Net Worth Calculator',
      subheading: 'See where you stand.',
      href: 'https://www.ramseysolutions.com/retirement/net-worth-calculator?lead_source=financial_peace',
      buttonText: 'Check It Out',
      attribution: 'trustedlead_fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/coverage-checkup.png',
      heading: 'Coverage Checkup',
      subheading: 'Learn what insurance you need to add or drop.',
      href: 'https://www.ramseysolutions.com/insurance/coverage-checkup?lead_source=financial_peace',
      buttonText: 'Get Started',
      attribution: 'trustedlead_fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/investment.png',
      heading: 'Investment Calculator',
      subheading: 'See what your investments could become.',
      href: 'https://www.ramseysolutions.com/retirement/investment-calculator?lead_source=financial_peace',
      buttonText: 'Check It Out',
      attribution: 'trustedlead_fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/mortgage-payoff.png',
      heading: 'Mortgage Payoff Calculator',
      subheading: 'Pay off your house quick.',
      href: 'https://www.ramseysolutions.com/real-estate/mortgage-payoff-calculator?lead_source=financial_peace',
      buttonText: 'Get Started',
      attribution: 'trustedlead_fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/debt-snowball-calculator-icon.png',
      heading: 'Debt Snowball Calculator',
      subheading: 'Find out your debt-free date and accelerate your progress.',
      href: 'https://www.ramseysolutions.com/debt/debt-calculator?snid=free-tools.debt.debt-snowball-calculator?lead_source=financial_peace',
      buttonText: 'Check It Out',
      attribution: 'fpu'
    },
    {
      icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/resources/house-icon.png',
      heading: 'Mortgage Calculator',
      subheading: 'See how much home you can afford.',
      href: 'https://www.ramseysolutions.com/real-estate/mortgage-calculator-tool?lead_source=financial_peace',
      buttonText: 'Get Started',
      attribution: 'trustedlead_fpu'
    }
  ]
};

export const PERSONALITY_MAP: { [key in Personality]: PersonalityDetails } = {
  'Dave Ramsey': {
    description:
      'I’m here to tell you that whoever you are, wherever you came from, and however deep of a mess you might be in right now, you can get out of it. And I want to walk with you through it, every step of the way. It’s go time.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/dave-pic.png'
  },
  'George Kamel': {
    description:
      'I’m on a mission to help people spend less, save more, and avoid money traps so they can build wealth and live life on their terms. Think of me like your slightly nerdy friend who delivers helpful money advice (with a healthy dose of snark).',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/george-pic.png'
  },
  'Jade Warshaw': {
    description:
      'What’s up, guys! My husband, Sam, and I paid off over $460,000 in debt. Ever since, I’ve been coaching others on how to pay off their own debt by shifting their mindset, beliefs and actions around money. I absolutely love talking to people about all things finance!',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/jade-pic.png'
  },
  'John Delony': {
    description:
      'What’s up, good folks! The official line is “I’m a mental health expert”—but all that means is I get the privilege of walking with you through the tough stuff. Because you’re worth being well—emotionally, relationally, and financially. I’m grateful you’re here.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/john-pic.png'
  },
  'Ken Coleman': {
    description:
      'Hey folks! I’m here to help you use what you do best and do work you love—so you can make the difference you were born to make! There are people in the world who need what you have to offer! So, let’s get going.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/ken-pic.png'
  },
  'Rachel Cruze': {
    description:
      'I love helping people get out of debt, take control of their money, build wealth, and actually have fun in the process! I can’t wait to cheer you on as you start winning with money and creating a life you love.',
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/rachel-pic.png'
  }
};

export const LESSON_NUMBER_TO_LESSON_ID: Record<LessonNumber, LessonId> = {
  1: 'baby-step-one-and-budgeting',
  2: 'baby-step-two',
  3: 'baby-step-three',
  4: 'baby-step-four-five-six-seven',
  5: 'wise-spending',
  6: 'understanding-insurance',
  7: 'building-wealth',
  8: 'buying-and-selling-your-home',
  9: 'outrageous-generosity'
};

export const TRUSTED_RESOURCES: TrustedCardProps[] = [
  {
    image: HomeAutoIcon,
    title: 'Protect Your Home and Auto',
    description: 'Save big when you bundle home and auto insurance.',
    tag: true,
    icon: LinkExternalIcon,
    link: 'https://www.ramseysolutions.com/insurance/home-and-auto-insurance?lead_source=financial_peace',
    referralDestination: 'Trusted - Protections'
  },
  {
    image: FamilyIcon,
    title: 'Protect Your Family',
    description: 'Create a will or get term life insurance.',
    tag: true,
    icon: ChevronDownIcon,
    border: true,
    children: [
      {
        image: TermLife,
        title: 'Term Life Insurance',
        description:
          'Term life insurance is the safest choice when it comes to protecting your family’s future.',
        link: 'https://www.ramseysolutions.com/insurance/term-life-insurance?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Term Life Insurance',
        button: true,
        buttonText: 'Start'
      },
      {
        image: RibbonIcon,
        title: 'Last Will & Testament',
        description:
          'Create your will online. No legal jargon. No hourly fees.',
        descTag: '20% Off With Financial Peace All Access',
        link: 'https://www.ramseysolutions.com/retirement/will?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Wills',
        border: true,
        button: true,
        buttonText: 'Start'
      }
    ]
  },
  {
    image: RetirementIcon,
    title: 'Invest for Retirement',
    description:
      'Get a list of up to five investing professionals in your area.',
    tag: true,
    icon: LinkExternalIcon,
    link: 'https://www.ramseysolutions.com/retirement/smartvestor?lead_source=financial_peace',
    referralDestination: 'Trusted - Investing and Tax',
    border: true
  },
  {
    image: ForSaleIcon,
    title: 'Work With a Home Expert',
    description: 'Buy, sell, or refinance your home with confidence.',
    icon: ChevronDownIcon,
    border: true,
    children: [
      {
        image: HomeBuySellIcon,
        title: 'Buy or Sell a Home',
        description:
          'Find the best agents in your area and tackle the market with confidence.',
        link: 'https://trusted.ramseysolutions.com/real-estate/referrals/form/start?continue&lead_source=financial_peace',
        referralDestination: 'Trusted - Real Estate - Buy and Sell',
        button: true,
        buttonText: 'Start'
      },
      {
        image: HomeLoanIcon,
        title: 'Home Loan',
        description: 'Work with a lender you can trust.',
        link: 'https://www.ramseysolutions.com/real-estate/mortgage-loans?lead_source=financial_peace',
        referralDestination: 'Trusted - Real Estate - Mortgage Company',
        border: true,
        button: true,
        buttonText: 'Start'
      },
      {
        image: HomeRefinancingIcon,
        title: 'Home Refinancing',
        description: 'Save thousands when you refinance the right way.',
        link: 'https://www.ramseysolutions.com/real-estate/mortgage-refinance?lead_source=financial_peace',
        referralDestination: 'Trusted - Real Estate',
        border: true,
        button: true,
        buttonText: 'Start'
      }
    ]
  },
  {
    image: TaxesIcon,
    title: 'Get Help With Taxes',
    description: 'Make tax season a breeze.',
    icon: ChevronDownIcon,
    border: true,
    children: [
      {
        image: SmartTaxIcon,
        title: 'SmartTax',
        description: 'Helpful, online tax-filing with no hidden fees.',
        descTag: 'Free Federal Filing With Financial Peace All Access',
        link: 'https://www.ramseysolutions.com/taxes/smarttax?lead_source=financial_peace',
        referralDestination: 'Trusted - Investing and Tax - SmartTax',
        button: true,
        buttonText: 'Start'
      },
      {
        image: TaxesCpaIcon,
        title: 'Certified Tax Accountant',
        description:
          'For all your tax needs, get seasonal to year-round support from a tax pro.',
        link: 'https://www.ramseysolutions.com/taxes/tax-advisor?lead_source=financial_peace',
        referralDestination: 'Trusted - Investing and Tax - Tax Pro',
        border: true,
        button: true,
        buttonText: 'Start'
      }
    ]
  },
  {
    image: HealthInsuranceIcon,
    title: 'Get Health Insurance',
    description: 'Keep your family healthy with the right plan.',
    icon: ChevronDownIcon,
    border: true,
    children: [
      {
        image: SubHealthInsuranceIcon,
        title: 'Health Insurance',
        description: 'Keep your family healthy with the right plan.',
        link: 'https://www.ramseysolutions.com/insurance/health-insurance?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Health Insurance',
        button: true,
        buttonText: 'Start'
      },
      {
        image: LongTermCareIcon,
        title: 'Long-Term Care Insurance',
        description:
          'Proactively cover the costs of a nursing home or at-home care.',
        link: 'https://www.ramseysolutions.com/insurance/long-term-care-insurance?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Long Term Care',
        border: true,
        button: true,
        buttonText: 'Start'
      },
      {
        image: MedicareServicesIcon,
        title: 'Medicare Services',
        description:
          'Understand your options and choose the right Medicare plan for you.',
        link: 'https://www.ramseysolutions.com/insurance/medicare?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Medicare',
        border: true,
        button: true,
        buttonText: 'Start'
      }
    ]
  },
  {
    image: IdTheft,
    title: 'Get Specialty Insurance',
    description:
      'Find coverage for situations that aren’t protected by traditional insurance.',
    icon: ChevronDownIcon,
    border: true,
    children: [
      {
        image: SpecialtyUmbrellaIcon,
        title: 'Umbrella Insurance',
        description:
          'Don’t let lawsuits rain on your parade. Cover your assets.',
        link: 'https://www.ramseysolutions.com/insurance/umbrella-insurance?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Property and Casualty',
        button: true,
        buttonText: 'Start'
      },
      {
        image: SpecialtyCommercialIcon,
        title: 'Commercial Insurance',
        description:
          'It’s business as usual—until it’s not. Protect your small business today.',
        link: 'https://www.ramseysolutions.com/insurance/business-insurance?lead_source=financial_peace',
        referralDestination: 'Trusted - Protections - Property and Casualty',
        border: true,
        button: true,
        buttonText: 'Start'
      },
      {
        image: SpecialtyIdTheftIcon,
        title: 'Identity Theft Protection',
        description: 'You’re the only you. Let’s keep it that way.',
        link: 'https://www.ramseysolutions.com/insurance/id-theft-protection?lead_source=financial_peace',
        referralDestination:
          'Trusted - Protections - Identity Theft Protection',
        border: true,
        button: true,
        buttonText: 'Start'
      }
    ]
  }
];

export const COORDINATOR_QUIZ_CALLOUT_DISMISSED =
  'coordinator_quiz_callout_dismissed';

export const BENEFITS_LIST = [
  {
    benefitLabel: 'Access to FPU online learning platform',
    isFpuBenefit: true,
    isRPlusBenefit: true
  },
  {
    benefitLabel: 'Join any in-person or virtual FPU class',
    isFpuBenefit: true,
    isRPlusBenefit: true
  },
  {
    benefitLabel: 'Access to EveryDollar Premium',
    durationFpu: '3 months',
    durationRplus: '12 months'
  },
  {
    benefitLabel: 'One year of group financial coaching',
    isFpuBenefit: true,
    isRPlusBenefit: true
  },
  {
    benefitLabel: 'A free one-on-one financial coaching session',
    isFpuBenefit: true,
    isRPlusBenefit: true
  },
  {
    benefitLabel: 'Fully editable digital FPU workbook',
    isFpuBenefit: true,
    isRPlusBenefit: true
  },
  {
    benefitLabel: '20+ Ramsey Audiobook',
    isFpuBenefit: false,
    isRPlusBenefit: true
  },
  {
    benefitLabel: 'Free federal tax filing software',
    isFpuBenefit: false,
    isRPlusBenefit: true
  },
  {
    benefitLabel: '50+ hours of exclusive video content',
    isFpuBenefit: false,
    isRPlusBenefit: true
  }
];

export const BABY_STEPS = {
  1: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-1-gem.png',
    description:
      'Your first goal is to save $1,000 for your starter emergency fund as fast as you possibly can. An emergency is going to happen, so you have to be ready when it hits.',
    heading: 'Save $1,000 for Your Starter Emergency Fund'
  },
  2: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-2-gem.png',
    description:
      'If you’ve got $1,000 in the bank, you’re ready for Baby Step 2: paying off all your debt using the debt snowball method! Attack the smallest debt while making minimum payments on the rest. Once it’s paid off, put that freed-up money on the next-smallest debt. Keep going until you’re debt-free!',
    heading: 'Pay Off All Debt (Except the House) Using the Debt Snowball'
  },
  3: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-3-gem.png',
    description:
      'You’ve paid off your debt! Way. To. Go. Next, you’ll take the money you were throwing at debt and save up a fully funded emergency fund. Because with 3-6 months of expenses sitting in the bank, you can turn any crisis into an inconvenience.',
    heading: 'Save 3-6 Months of Expenses in a Fully Funded Emergency Fund'
  },
  '3b': {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-3b-gem.png',
    description:
      'If you’re debt-free with your emergency fund in place and don’t own a home yet, Baby Step 3b is your next move! Here, you’ll start saving for a solid down payment so you can buy a home the smart way—without breaking your budget.',
    heading: 'Save for a Down Payment on Your First Home'
  },
  4: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-4-gem.png',
    description:
      "Once you're debt-free with a fully funded emergency fund, it's time for the really good part—building wealth for the future! In Baby Step 4, you'll invest 15% of your income for your retirement.",
    heading: 'Invest 15% of Your Household Income in Retirement'
  },
  5: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-5-gem.png',
    description:
      "By this step, you've paid off all debts (except the house) and started saving for retirement. Next, it's time to save for your children's college expenses so they can go to college the right way—without debt!",
    heading: "Save for Your Children's College Fund"
  },
  6: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-6-gem.png',
    description:
      'Emergency fund? Check. Retirement and college fund on track? Check. Now, your mortgage is the only thing between you and complete freedom from debt. Just imagine your life with no house payment!',
    heading: 'Pay Off Your Home Early'
  },
  7: {
    image: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/bs-7-gem.png',
    description:
      'You know what people with no debt can do? Anything they want! Now, you can truly live and give like no one else by building wealth, leaving a legacy, and becoming insanely generous (aka the most fun you can have with money)!',
    heading: 'Build Wealth and Give'
  }
};

export const TURNAROUND_ITEM_MAP = {
  debt: {
    heading: 'Debt Paid',
    icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/debt-snowball.png',
    iconColor: '#adffff',
    iconBgColor: '#D8F1FC',
    initialText: 'Initial Debt',
    initialLabelText: 'Total Debt When You Started FPU',
    initialRequiredError: 'Initial debt is required',
    progressiveLabelText: 'Recent Debt Paid Off',
    proTip:
      'This includes credit cards, student loans, car loans, second mortgages, home equity loans, etc. (This <strong class="font-semibold">does not</strong> include your primary mortgage.)',
    winLabel: 'Select a Debt Payoff Win'
  },
  savings: {
    heading: 'Money Saved',
    icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/savings.png',
    iconColor: '#35BD59',
    iconBgColor: '#35BD59',
    initialText: 'Initial Savings',
    initialLabelText: 'Total Savings When You Started FPU',
    initialRequiredError: 'Initial savings is required',
    progressiveLabelText: 'Recent Savings Added',
    proTip:
      'This includes money you have in checking, savings, and money market accounts.',
    winLabel: 'Select a Savings Win'
  },
  cards: {
    heading: 'Credit Cards Cut',
    icon: 'https://cdn.ramseysolutions.net/media/fpu/fpu_member/financial-turnaround/illustrations/credit-cards-cut.png',
    iconColor: '#E64B40',
    iconBgColor: '#FFC4B8',
    initialText: 'Initial Cards',
    initialLabelText: 'Initial Credit Card Count',
    initialRequiredError: 'Initial credit card count is required',
    progressiveLabelText: 'Recent Cards Cut',
    proTip: 'Even credit cards with zero balance.'
  }
};

export const NO_AVAILABLE_ORG_CLASSES_DISMISSED =
  'no_available_org_classes_dismissed';
